import { Injectable, signal, computed, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ApiService } from '../../shared/data-access/api.service';
import { environment } from '../../../environments/environment';
import { Company } from '../../shared/models/company.model';
import { LoginResponse, Session } from '../models/auth.model';
import { ThemeService } from '../../layouts/data-access/theme.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly SESSION_KEY = 'ng_audit_session';
  private api = inject(ApiService);
  private themeService = inject(ThemeService);
  private sessionSignal = signal<Session | null>(this.loadSession());

  readonly session = this.sessionSignal.asReadonly();
  readonly isAuthenticated = computed(() => !!this.sessionSignal());
  readonly token = computed(() => this.sessionSignal()?.token ?? null);
  readonly coduser = computed(() => this.sessionSignal()?.coduser ?? null);
  readonly selectedCompany = computed(() => this.sessionSignal()?.selectedCompany ?? null);

  login(coduser: string, clave: string, ruc: string): Observable<LoginResponse> {
    return this.api.post<LoginResponse>(`${environment.API_URL_LOGIN}/usuario/auth-config`, { coduser, clave: btoa(clave), ruc }).pipe(
      tap(res => {
        const session: Session = { token: res.token, coduser: res.coduser, idSession: res.idSession };
        this.saveSession(session);
      })
    );
  }

  getCompanies(): Observable<Company[]> {
    return this.api.get<Company[]>(`${environment.API_URL_CONFIG}/company/buscar/lite-config?soloActivos=true`);
  }

  selectCompany(company: Company): void {
    const session = this.sessionSignal();
    if (session) {
      this.saveSession({ ...session, selectedCompany: company });
    }
  }

  logout(): void {
    this.clearSession();
    // El tema nunca debe sobrevivir a una sesión: siempre vuelve a Default (claro).
    this.themeService.setTheme('light');
  }

  private loadSession(): Session | null {
    try {
      const raw = localStorage.getItem(this.SESSION_KEY);
      return raw ? (JSON.parse(raw) as Session) : null;
    } catch {
      return null;
    }
  }

  private saveSession(session: Session): void {
    this.sessionSignal.set(session);
    localStorage.setItem(this.SESSION_KEY, JSON.stringify(session));
  }

  clearSession(): void {
    this.sessionSignal.set(null);
    localStorage.removeItem(this.SESSION_KEY);
  }
}
